import { baseApi } from "../baseApi";

const contractsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getContracts: builder.query({
      query: (params = {}) => ({
        url: "/api/contracts",
        params,
      }),
      providesTags: ["Contract"],
    }),

    getContract: builder.query({
      query: (id) => `/api/contracts/${id}`,
      providesTags: (result, error, id) => [{ type: "Contract", id }],
    }),

    getContractHistory: builder.query({
      query: (id) => `/api/contracts/${id}/history`,
      providesTags: (result, error, id) => [{ type: "ContractHistory", id }],
    }),

    getArticleHistory: builder.query({
      query: (articleId) => `/api/contracts/articles/${articleId}/history`,
      providesTags: (result, error, articleId) => [
        { type: "ArticleHistory", id: articleId },
      ],
    }),

    getContractMous: builder.query({
      query: (contractId) => `/api/contracts/${contractId}/mous`,
      providesTags: (result, error, contractId) => [
        { type: "MOU", id: contractId },
      ],
    }),

    createContract: builder.mutation({
      query: (body) => ({
        url: "/api/contracts",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Contract"],
    }),

    updateContract: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/contracts/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Contract", id },
        "Contract",
      ],
    }),

    deleteContract: builder.mutation({
      query: (id) => ({
        url: `/api/contracts/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Contract"],
    }),

    startNegotiating: builder.mutation({
      query: (id) => ({
        url: `/api/contracts/${id}/start-negotiating`,
        method: "POST",
      }),
      invalidatesTags: ["Contract"],
    }),

    ratifyContract: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/contracts/${id}/ratify`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Contract"],
    }),

    createArticle: builder.mutation({
      query: ({ contractId, ...body }) => ({
        url: `/api/contracts/${contractId}/articles`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: "Contract", id: contractId },
      ],
    }),

    updateArticle: builder.mutation({
      query: ({ contractId, articleId, ...body }) => ({
        url: `/api/contracts/${contractId}/articles/${articleId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: "Contract", id: contractId },
      ],
    }),

    deleteArticle: builder.mutation({
      query: ({ contractId, articleId }) => ({
        url: `/api/contracts/${contractId}/articles/${articleId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: "Contract", id: contractId },
      ],
    }),

    toggleArticleTa: builder.mutation({
      query: ({ contractId, articleId }) => ({
        url: `/api/contracts/${contractId}/articles/${articleId}/ta`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: "Contract", id: contractId },
      ],
    }),

    reorderArticles: builder.mutation({
      query: ({ contractId, order }) => ({
        url: `/api/contracts/${contractId}/articles/reorder`,
        method: "PUT",
        body: { order },
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: "Contract", id: contractId },
      ],
    }),

    createMou: builder.mutation({
      query: ({ contractId, ...body }) => ({
        url: `/api/contracts/${contractId}/mous`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: "MOU", id: contractId },
        { type: "Contract", id: contractId },
      ],
    }),

    updateMou: builder.mutation({
      query: ({ contractId, mouId, ...body }) => ({
        url: `/api/contracts/${contractId}/mous/${mouId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: "MOU", id: contractId },
      ],
    }),

    deleteMou: builder.mutation({
      query: ({ contractId, mouId }) => ({
        url: `/api/contracts/${contractId}/mous/${mouId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: "MOU", id: contractId },
        { type: "Contract", id: contractId },
      ],
    }),

    updateDepartmentContractSettings: builder.mutation({
      query: ({ departmentId, ...body }) => ({
        url: `/api/departments/${departmentId}/contract-settings`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Contract"],
    }),

    // Proposals
    getProposals: builder.query({
      query: ({ contractId, articleId }) =>
        `/api/contracts/${contractId}/articles/${articleId}/proposals`,
      providesTags: (result, error, { articleId }) => [
        { type: "Proposal", id: articleId },
      ],
    }),

    createProposal: builder.mutation({
      query: ({ contractId, articleId, ...body }) => ({
        url: `/api/contracts/${contractId}/articles/${articleId}/proposals`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { articleId }) => [
        { type: "Proposal", id: articleId },
      ],
    }),

    updateProposal: builder.mutation({
      query: ({ contractId, articleId, proposalId, ...body }) => ({
        url: `/api/contracts/${contractId}/articles/${articleId}/proposals/${proposalId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { articleId }) => [
        { type: "Proposal", id: articleId },
      ],
    }),

    createCounter: builder.mutation({
      query: ({ contractId, articleId, proposalId, ...body }) => ({
        url: `/api/contracts/${contractId}/articles/${articleId}/proposals/${proposalId}/counters`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { articleId }) => [
        { type: "Proposal", id: articleId },
      ],
    }),

    // Notes
    getNotes: builder.query({
      query: ({ contractId, articleId }) =>
        `/api/contracts/${contractId}/articles/${articleId}/notes`,
      providesTags: (result, error, { articleId }) => [
        { type: "BargainingNote", id: articleId },
      ],
    }),

    createNote: builder.mutation({
      query: ({ contractId, articleId, ...body }) => ({
        url: `/api/contracts/${contractId}/articles/${articleId}/notes`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { articleId }) => [
        { type: "BargainingNote", id: articleId },
      ],
    }),

    deleteNote: builder.mutation({
      query: ({ contractId, articleId, noteId }) => ({
        url: `/api/contracts/${contractId}/articles/${articleId}/notes/${noteId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { articleId }) => [
        { type: "BargainingNote", id: articleId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetContractsQuery,
  useGetContractQuery,
  useGetContractHistoryQuery,
  useGetArticleHistoryQuery,
  useGetContractMousQuery,
  useCreateContractMutation,
  useUpdateContractMutation,
  useDeleteContractMutation,
  useStartNegotiatingMutation,
  useRatifyContractMutation,
  useCreateArticleMutation,
  useUpdateArticleMutation,
  useDeleteArticleMutation,
  useToggleArticleTaMutation,
  useReorderArticlesMutation,
  useCreateMouMutation,
  useUpdateMouMutation,
  useDeleteMouMutation,
  useUpdateDepartmentContractSettingsMutation,
  useGetProposalsQuery,
  useCreateProposalMutation,
  useUpdateProposalMutation,
  useCreateCounterMutation,
  useGetNotesQuery,
  useCreateNoteMutation,
  useDeleteNoteMutation,
} = contractsApi;
